/**
 * Address data extractor and standardizer — base class.
 *
 * Provides shared logic (static helpers, constructor scaffold, freeze) for all
 * provider-specific address extractor subclasses.
 *
 * @module data/AddressExtractor
 * @since 0.9.0-alpha
 * @author Marcelo Pereira Barbosa
 */

import BrazilianStandardAddress from './BrazilianStandardAddress.js';
import ReferencePlace from './ReferencePlace.js';
import type { NominatimAddress, NominatimResponse, OsmElement } from '../types/nominatim.js';

/** Brazilian state full names → two-letter UF abbreviations. Used as a fallback
 *  when the geocoding provider does not supply a state_code or ISO3166-2-lvl4 field. */
const BRAZIL_STATE_SIGLAS = new Map([
	['Acre', 'AC'], ['Alagoas', 'AL'], ['Amapá', 'AP'], ['Amazonas', 'AM'],
	['Bahia', 'BA'], ['Ceará', 'CE'], ['Distrito Federal', 'DF'],
	['Espírito Santo', 'ES'], ['Goiás', 'GO'], ['Maranhão', 'MA'],
	['Mato Grosso', 'MT'], ['Mato Grosso do Sul', 'MS'], ['Minas Gerais', 'MG'],
	['Pará', 'PA'], ['Paraíba', 'PB'], ['Paraná', 'PR'], ['Pernambuco', 'PE'],
	['Piauí', 'PI'], ['Rio de Janeiro', 'RJ'], ['Rio Grande do Norte', 'RN'],
	['Rio Grande do Sul', 'RS'], ['Rondônia', 'RO'], ['Roraima', 'RR'],
	['Santa Catarina', 'SC'], ['São Paulo', 'SP'], ['Sergipe', 'SE'],
	['Tocantins', 'TO'],
]);

/**
 * Base class for extracting geocoding API responses into a standardized
 * Brazilian address format.
 *
 * Subclasses must implement `padronizaEndereco()` to handle their own
 * provider's field names.
 *
 * @class
 * @abstract
 */
class AddressExtractor {
	data: Record<string, unknown>;
	enderecoPadronizado: BrazilianStandardAddress;

	/**
	 * @param {Object} data - Raw address data from a geocoding API
	 */
	constructor(data: Record<string, unknown>) {
		this.data = data;
		this.enderecoPadronizado = new BrazilianStandardAddress();
		this.padronizaEndereco();
		Object.freeze(this);
	}

	/**
	 * Maps raw provider data into `this.enderecoPadronizado`.
	 * Must be implemented by each subclass.
	 *
	 * @abstract
	 */
	padronizaEndereco(): void {
		// implemented by subclasses
	}

	/**
	 * Extracts the state abbreviation from an ISO 3166-2 level-4 code.
	 *
	 * @param {string} iso3166Code - e.g. "BR-SP"
	 * @returns {string|null} e.g. "SP", or null if invalid
	 */
	static extractSiglaUF(iso3166Code: string | undefined): string | null {
		if (!iso3166Code || typeof iso3166Code !== 'string') return null;
		const match = iso3166Code.match(/^BR-([A-Z]{2})$/);
		return match ? match[1] : null;
	}

	/** Maps a full Brazilian state name (e.g. "São Paulo") to its UF abbreviation ("SP"). */
	static resolveStateSigla(stateName: string | null | undefined): string | null {
		if (!stateName) return null;
		return BRAZIL_STATE_SIGLAS.get(stateName) ?? null;
	}

	static normalizeComparisonValue(value: string | null | undefined): string | null {
		if (typeof value !== 'string') return null;
		const normalized = value.trim().replace(/\s+/g, ' ');
		return normalized === '' ? null : normalized.toLocaleLowerCase('pt-BR');
	}

	toString(): string {
		return `${this.constructor.name}: ${this.enderecoPadronizado.enderecoCompleto()}`;
	}
}

// ---------------------------------------------------------------------------
// NominatimAddressExtractor — parses OpenStreetMap Nominatim API responses
// ---------------------------------------------------------------------------

/**
 * Extracts and standardizes address data from OpenStreetMap Nominatim API responses.
 *
 * Supports both Nominatim API format (road, suburb, city, postcode, …) and
 * standard OSM address tags (addr:street, addr:housenumber, …).
 *
 * @class
 * @extends AddressExtractor
 * @immutable Instances are frozen after construction
 */
class NominatimAddressExtractor extends AddressExtractor {
	declare data: NominatimResponse;

	/**
	 * Maps Nominatim fields into the standardized Brazilian address.
	 *
	 * @override
	 */
	padronizaEndereco(): void {
		if (!this.data) return;

		let address: NominatimAddress;
		let isOsmElement = false;

		if (this.data.address) {
			// Nominatim nested format: { address: { road, city, … } }
			address = this.data.address as NominatimAddress;
			isOsmElement = true;
		} else if (this.data.city || this.data.street || this.data.neighborhood) {
			// Flat GeoAddress format from paraty_geoservices v1.6.x:
			// { street, streetNumber, neighborhood, city, state, stateCode, postalCode, country }
			address = {
				street:       this.data.street       as string | undefined,
				house_number: this.data.streetNumber  as string | undefined,
				neighbourhood: this.data.neighborhood as string | undefined,
				city:          this.data.city          as string | undefined,
				county:        this.data.metropolitanRegion as string | undefined,
				state:         this.data.state         as string | undefined,
				state_code:    this.data.stateCode     as string | undefined,
				postcode:      this.data.postalCode    as string | undefined,
				country:       this.data.country       as string | undefined,
			} as NominatimAddress;
		} else {
			return;
		}

		this.enderecoPadronizado.logradouro =
			address['addr:street'] || address.road || address.street || address.pedestrian || null;

		this.enderecoPadronizado.numero =
			address['addr:housenumber'] || address.house_number || null;

		const bairro =
			address['addr:neighbourhood'] || address.neighbourhood || address.suburb || address.quarter || null;
		const municipio =
			address['addr:city'] || address.city || address.town || address.municipality || address.village || null;
		const distrito = address.city_district || null;
		const redundantDistrito =
			AddressExtractor.normalizeComparisonValue(distrito) !== null &&
			AddressExtractor.normalizeComparisonValue(distrito) ===
				AddressExtractor.normalizeComparisonValue(municipio);

		this.enderecoPadronizado.bairro = bairro;
		// Nominatim may echo the municipality name in city_district; keep only meaningful distrito values.
		this.enderecoPadronizado.distrito = redundantDistrito ? null : distrito;
		this.enderecoPadronizado.municipio = municipio;

		this.enderecoPadronizado.regiaoMetropolitana = address.county || null;

		this.enderecoPadronizado.uf =
			address['addr:state'] || address.state || null;

		this.enderecoPadronizado.siglaUF =
			address.state_code || AddressExtractor.extractSiglaUF(address['ISO3166-2-lvl4']) || null;

		// Edge case: uf contains a two-letter code already
		if (this.enderecoPadronizado.uf && /^[A-Z]{2}$/.test(this.enderecoPadronizado.uf)) {
			this.enderecoPadronizado.siglaUF = this.enderecoPadronizado.uf;
		}

		// Fallback: some providers (ibira.js flat format) omit state_code and ISO3166-2-lvl4.
		// Resolve from the full state name when both are absent.
		if (!this.enderecoPadronizado.siglaUF && this.enderecoPadronizado.uf) {
			this.enderecoPadronizado.siglaUF = AddressExtractor.resolveStateSigla(this.enderecoPadronizado.uf);
		}

		this.enderecoPadronizado.cep =
			address['addr:postcode'] || address.postcode || null;

		this.enderecoPadronizado.pais =
			address.country === 'Brasil' || address.country === 'Brazil'
				? 'Brasil'
				: (address.country || 'Brasil');

		if (isOsmElement) {
			this.enderecoPadronizado.referencePlace = new ReferencePlace(this.data as OsmElement);
		}
	}
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

// Default export is NominatimAddressExtractor so all existing callers
// (AddressCache, AddressDataExtractor, guia.js) continue to work unchanged.
export default NominatimAddressExtractor;

export { AddressExtractor, NominatimAddressExtractor };
